import React, { useContext, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'LuxKit/Button/TextButton'
import WalledContent from 'components/Common/WalledContent'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { useAppSelector } from 'hooks/reduxHooks'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useBedbankRates, { useBedbankRatesForIndividualRooms } from 'hooks/useBedbankRates'
import { getActiveHotelOnlyAndBundlePackagesByRoom } from 'selectors/offerPage/bedbankMultipleRoomBookingSelectors'
import type { RoomWithData } from 'hooks/OfferPage/useBedbankBuyCallbacks'
import BedbankSummaryPriceDetailsIndividualRooms from './BedbankSummaryPriceDetails/BedbankSummaryPriceDetailsIndividualRooms'
import BedbankSummaryPriceDetailsSameRoom from './BedbankSummaryPriceDetails/BedbankSummaryPriceDetailsSameRoom'
import useBedbankOfferSell from 'hooks/useBedbankOfferSell'

const SummaryPane = styled(Pane)`
  position: relative;
  min-width: ${rem(342)};
`

const RelativeBlock = styled.div`
  position: relative;
`

interface Props {
  checkInDate?: string
  checkOutDate?: string
  className?: string
  offer: App.BedbankOffer
  onViewPackageOptions?: () => void
  onCheckAvailabilityClick?: () => void
  rooms: Array<OfferPageRoom>
  flightPrice?: number
  flightsEnabled: boolean
  duration: number
  loadingPrices?: boolean
  isMultiRoomIndividualMode: boolean
}

function BedbankOfferSummary(props: Props) {
  const {
    checkInDate,
    checkOutDate,
    className,
    offer,
    onViewPackageOptions,
    onCheckAvailabilityClick,
    rooms,
    loadingPrices,
    flightsEnabled,
    duration,
    flightPrice,
    isMultiRoomIndividualMode,
  } = props
  const intl = useIntl()
  const priceTrackedRef = useRef(false)

  const offerPageState = useContext(OfferPageStateContext)

  // Trigger per-room rate fetching so the selectors have data.
  // The fetch is idempotent - if rates are already loaded this is a no-op.
  const [, individualRatesFetching] = useBedbankRatesForIndividualRooms(
    isMultiRoomIndividualMode ? offer.id : undefined,
    rooms,
    checkInDate,
    checkOutDate,
    isMultiRoomIndividualMode,
  )

  // Read per-room selections from the selector (memoized by reselect)
  const selectedPackages = useAppSelector(appState =>
    getActiveHotelOnlyAndBundlePackagesByRoom(offerPageState, appState),
  )

  // Build RoomWithData[] from the selector result for BedbankSummaryPriceDetailsIndividualRooms
  const roomsWithData = useMemo(() => {
    if (individualRatesFetching || !isMultiRoomIndividualMode) return undefined

    const rwd: Array<RoomWithData> = []
    for (const room of rooms) {
      const hotelOnly = selectedPackages.hotelOnly[room.id]
      const bundle = selectedPackages.bundle[room.id]
      if (!hotelOnly?.rate) continue

      rwd.push({
        room,
        package: hotelOnly.package,
        rate: hotelOnly.rate,
        bundleRate: bundle?.rate ?? hotelOnly.rate,
        effectiveRate: hotelOnly.rate,
        promotion: undefined,
        capacityValid: true,
      })
    }

    return rwd.length > 0 ? rwd : undefined
  }, [individualRatesFetching, isMultiRoomIndividualMode, rooms, selectedPackages])

  const hasDates = checkInDate && checkOutDate

  // Same room
  const [{ hotelOnlyRates, hotelBundleRates }, loadingSameRoomRates] = useBedbankRates(offer.id, rooms, checkInDate, checkOutDate)
  const sell = useBedbankOfferSell(offer, duration, rooms)
  const hasRates = hotelOnlyRates.length > 0

  const lowestRate = hotelOnlyRates.reduce((lowest, current) => {
    return lowest.totals.inclusive < current.totals.inclusive ? lowest : current
  }, hotelOnlyRates[0])

  const lowestBundleRate = hotelBundleRates.reduce((lowest, current) => {
    return lowest.totals.inclusive < current.totals.inclusive ? lowest : current
  }, hotelBundleRates[0])

  const showPriceDetails = isMultiRoomIndividualMode ? !!roomsWithData : ((hasDates && hasRates) || (!hasDates && sell))
  const isSoldOut = isMultiRoomIndividualMode ? roomsWithData?.length !== rooms.length : hotelOnlyRates.length == 0 && hasDates
  const loading = isMultiRoomIndividualMode ? individualRatesFetching : loadingSameRoomRates

  const walledContentButtonText = hasDates ? intl.formatMessage({
    id: 'zfRDDs2ZCpi2D-m8cabZ',
    defaultMessage: 'View package options',
    description: 'View package options button text for bedbank offer summary when dates are selected',
  }) : intl.formatMessage({
    id: 'FOv4nOfdBLBhaGYHnSan',
    defaultMessage: 'Check availability',
    description: 'Check availability button text for bedbank offer summary when dates are not selected',
  })

  return (
    <SummaryPane
      border="neutral-six"
      borderRadius="XS"
      padding="28 20"
      shadow="bottom-small"
      className={className}
      data-testid="SummaryPane"
    >
      <Group direction="vertical" gap={12}>
        <WalledContent
          enforced={offer.walledGarden}
          hidePricing={offer.hidePricing}
          wall={<TextButton kind="primary" fit="flex" onClick={hasDates ? onViewPackageOptions : onCheckAvailabilityClick}>
            {walledContentButtonText}
          </TextButton>}
        >
          {showPriceDetails &&
            <RelativeBlock>
              <LoadingIndicator floating opaque backdrop="grey-light" visible={loadingPrices} />
              {isMultiRoomIndividualMode && roomsWithData && <BedbankSummaryPriceDetailsIndividualRooms
                roomsWithData={roomsWithData}
                rooms={rooms}
                offer={offer}
                duration={duration}
                flightPrice={flightPrice}
                flightsEnabled={flightsEnabled}
                onViewPackageOptions={onViewPackageOptions}
                priceTrackedRef={priceTrackedRef}
              />}
              {!isMultiRoomIndividualMode && <BedbankSummaryPriceDetailsSameRoom
                rate={lowestRate}
                bundleRate={lowestBundleRate}
                rooms={rooms}
                offer={offer}
                duration={duration}
                flightPrice={flightPrice}
                flightsEnabled={flightsEnabled}
                onViewPackageOptions={onViewPackageOptions}
                priceTrackedRef={priceTrackedRef}
              />}
            </RelativeBlock>}
          {!isSoldOut && hasDates && <TextButton kind="primary" fit="flex" onClick={onViewPackageOptions}>
            {intl.formatMessage({
              id: 'zfRDDs2ZCpi2D-m8cabZ',
              defaultMessage: 'View package options',
              description: 'View package options button text for bedbank offer summary when dates are selected',
            })}
          </TextButton>}
          {
            !hasDates && <TextButton kind="primary" fit="flex" onClick={onCheckAvailabilityClick}>
              {intl.formatMessage({
                id: 'FOv4nOfdBLBhaGYHnSan',
                defaultMessage: 'Check availability',
                description: 'Check availability button text for bedbank offer summary when dates are not selected',
              })}
            </TextButton>
          }
          {isSoldOut && !loading && <>
            <BodyText
              variant="large"
              weight="bold"
            >
              {intl.formatMessage({
                id: 'hRujkfxgS9zqnqmnClGU',
                defaultMessage: 'Sold out on your dates',
                description: 'Sold out message for bedbank offer summary when no availability',
              })}
            </BodyText>
            <TextButton kind="primary" fit="flex" to="/hotels">
              {intl.formatMessage({
                id: 'El1x3dyUI-brkGUZtEeh',
                defaultMessage: 'Browse all hotel offers',
                description: 'Browse all hotel offers button text for bedbank offer summary when sold out',
              })}
            </TextButton>
          </>}
        </WalledContent>
      </Group>
    </SummaryPane>
  )
}

export default React.memo(BedbankOfferSummary)
